<template>
   <div
      class="bg-light-grey inset-y-0 z-40 flex w-[250px] flex-shrink-0 flex-col p-2 transition-transform duration-300 max-md:fixed max-md:translate-x-[-100%]"
      :class="{
         '!translate-x-0': sidebarStore.$state.open,
      }"
   >
      <Dropdown v-model="organizationDropdownOpen" minTriggerWidth>
         <template #trigger="{ open, toggle }">
            <div
               class="__clickable rounded-small hover:bg-primary/10 flex items-center justify-between p-2 transition-all"
               :class="{
                  'bg-primary/10': open,
               }"
               @click="toggle"
            >
               <div class="flex items-center gap-2">
                  <div class="relative size-8 shrink-0">
                     <div class="rounded-small size-full overflow-hidden">
                        <Image
                           class="size-full bg-white object-cover"
                           :src="getFileUrlFromBucket(organization.data.value?.[0]?.propic_url)"
                        />
                     </div>
                     <span
                        v-if="hasUnreadNotifications"
                        class="bg-red border-light-grey absolute -top-1 -right-1 size-3 rounded-full border-2"
                     />
                  </div>

                  <p class="h-m line-clamp-2 overflow-hidden py-[2px] leading-[15px]">
                     {{ organization.data.value?.[0]?.translated_name }}
                  </p>
               </div>
               <Icon
                  name="chevron-down"
                  class="text-grey size-4 shrink-0 transition-[rotate]"
                  :class="{ '-rotate-180': open }"
               />
            </div>
         </template>
         <template #content>
            <div class="flex flex-col">
               <ul class="flex flex-col">
                  <template v-for="(item, index) in dropdown">
                     <li>
                        <MenuItem v-bind="item" @click="item.action" />
                     </li>
                     <li v-if="index === 0">
                        <MenuItem
                           data-name="notifications-trigger"
                           icon="bell"
                           :title="$t('common.notifications')"
                           :selected="notificationsOpen"
                           :dot="hasUnreadNotifications"
                           @click="openNotifications"
                        />
                     </li>
                  </template>
               </ul>

               <Separator />

               <MenuItem
                  v-if="isGroupAdmin"
                  icon="swatch"
                  :title="$t('common.impersonate')"
                  @click="impersonateModalOpen = true"
               />

               <MenuItem
                  icon="arrows-right-left"
                  :title="$t('common.change-account')"
                  :to="$localePath('/auth/redirect')"
               />

               <MenuItem
                  v-if="impersonating && backToDashboardID"
                  :title="$t('common.back-to-dashboard')"
                  icon="arrow-left"
                  type="destructive"
                  :to="$localePath(`/dashboard/${backToDashboardID}`)"
               />
               <MenuItem
                  icon="esc"
                  :title="$t('common.sign-out')"
                  :to="$localePath('/auth/sign-out')"
                  type="destructive"
               />
            </div>
         </template>
      </Dropdown>

      <!-- <MenuItem icon="bell" :title="$t('common.notifications')" disabled /> -->

      <Separator :showLine="false" />

      <ul class="flex flex-col">
         <li v-for="item in ungroupedMenu">
            <MenuItem v-bind="item" :hidden="!item.visible" :selected="selected(item.to)" />
         </li>

         <li v-for="section in menuSections">
            <MenuItem
               :icon="section.icon"
               :title="section.title"
               iconAfter="chevron-down"
               smallIconAfter
               :expanded="!!sidebarStore.$state.openGroups[section.key]"
               :selected="section.key === activeGroupKey"
               :onIcon="() => sidebarStore.toggleGroup(section.key)"
               @click="sidebarStore.toggleGroup(section.key)"
            />

            <div
               class="grid overflow-hidden transition-[grid-template-rows] duration-200"
               :class="sidebarStore.$state.openGroups[section.key] ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'"
            >
               <ul class="menu-tree flex min-h-0 flex-col overflow-hidden">
                  <li v-for="item in section.items" class="menu-tree__item">
                     <MenuItem v-bind="item" :icon="undefined" small :selected="selected(item.to)" />
                  </li>
               </ul>
            </div>
         </li>
      </ul>

      <div class="mt-auto flex flex-col gap-3">
         <HeaderLanguage dashboard />

         <div class="flex items-center gap-2">
            <div class="rounded-xsmall size-6 shrink-0 overflow-hidden">
               <Image
                  :src="getFileUrlFromBucket(jwt?.user_details?.propic_url)"
                  class="size-full object-cover"
               />
            </div>
            <div class="flex flex-col">
               <p class="p-s line-clamp-1">
                  {{ jwt?.customer_details?.full_name || jwt?.user_details?.email }}
               </p>
               <p class="p-s opacity-20">
                  {{ jwt?.user_details?.id }}
               </p>
            </div>
         </div>
      </div>
   </div>

   <SidebarNotifications
      :open="notificationsOpen"
      :notifications="notifications.data.value || []"
      :loading="notifications.isFetching.value"
      @close="notificationsOpen = false"
   />

   <ImpersonateModal v-if="isGroupAdmin" v-model="impersonateModalOpen" />

   <OrganizationModal v-model="organizationModalOpen" />
</template>

<script lang="ts" setup>
import { getFileUrlFromBucket } from '~/composables/api/helpers/get-file-url'
import { useOrganization } from '~/composables/api/queries/organization'
import { useOrganizationNotification } from '~/composables/api/queries/organization-notification'
import { useGetJwt } from '~/composables/api/supabase/get-jwt'
import { useSidebarStore } from '~/stores/sidebar-store'
import { BACK_TO_DASHBOARD_ID, IMPERSONATING } from '~/utils/consts/cookie-keys'

const { t, locale } = useI18n()
const localePath = useLocalePath()
const route = useRoute()
const jwt = useGetJwt()
const impersonating = useCookie<boolean>(IMPERSONATING)
const backToDashboardID = useCookie<string>(BACK_TO_DASHBOARD_ID)
const sidebarStore = useSidebarStore()

const organizationId = computed(() => route.params.id as string)
const organizationType = useOrganizationType()

const isOffice = computed(() => organizationType.value === 'OT_OFFICE')
const rbac = useRbac()
const impersonateModalOpen = ref<boolean>(false)
const organizationModalOpen = ref<boolean>(false)

const notificationsOpen = ref<boolean>(false)

const notifications = useOrganizationNotification(
   computed(() => ({
      organizationId: organizationId.value,
      enabled: !!organizationId.value,
   }))
)

const hasUnreadNotifications = computed(() =>
   (notifications.data.value || []).some((n) => !n.is_read)
)

const organizationDropdownOpen = ref<boolean>(false)

const openNotifications = () => {
   organizationDropdownOpen.value = false
   notificationsOpen.value = true
   sidebarStore.$state.open = false
   notifications.refetch()
}

const isGroupAdmin = computed(
   () => !!jwt.value?.organization_group_memberships?.some((m) => m.is_admin)
)

const organization = useOrganization(
   computed(() => ({
      id: organizationId.value,
      enabled: !!organizationId.value,
   }))
)

const selected = (name: string) => {
   return name === '/' ? !!menu.find((v) => route.path.endsWith(name)) : route.path.endsWith(name)
}

const menu = useMenuItemData(() => [
   {
      icon: 'home',
      title: t('web.tabs.recap'),
      to: localePath(`/dashboard/${organizationId.value}/`),
      visible: true,
      group: undefined,
   },
   {
      icon: 'chart-bar',
      title: t('web.tabs.statistics'),
      to: localePath(`/dashboard/${organizationId.value}/statistics`),
      visible: true,
      group: undefined,
   },
   {
      icon: 'map',
      title: t('web.tabs.locations'),
      to: localePath(`/dashboard/${organizationId.value}/locations`),
      visible: !isOffice.value,
      group: 'waters',
   },
   {
      icon: 'map',
      title: t('web.tabs.locations-by-number'),
      to: localePath(`/dashboard/${organizationId.value}/locations-by-number`),
      visible: isOffice.value,
      group: 'waters',
   },
   {
      icon: 'map-pin',
      title: t('web.tabs.management-sections'),
      to: localePath(`/dashboard/${organizationId.value}/management-sections`),
      visible: isOffice.value && rbac(['managementsection:view']),
      group: 'cultivation',
   },
   {
      icon: 'document-check',
      title: t('web.tabs.cultivation-plans'),
      to: localePath(`/dashboard/${organizationId.value}/cultivation-plans`),
      visible: rbac(['cultivationplan:view']),
      group: 'cultivation',
   },
   {
      icon: 'archive-box',
      title: t('web.tabs.points'),
      to: localePath(`/dashboard/${organizationId.value}/points`),
      visible: isOffice.value,
      group: 'waters',
   },
   {
      icon: 'ticket',
      title: t('web.tabs.products'),
      to: localePath(`/dashboard/${organizationId.value}/products`),
      visible: !isOffice.value,
      group: 'waters',
   },
   {
      icon: 'credit-card',
      title: t('web.tabs.permits'),
      to: localePath(`/dashboard/${organizationId.value}/permits`),
      visible: !isOffice.value,
      group: 'waters',
   },
   {
      icon: 'users',
      title: t('web.tabs.members'),
      to: localePath(`/dashboard/${organizationId.value}/members`),
      visible: !isOffice.value || organizationId.value === 'org_PwttcUFpJhbsvX',
      group: 'people',
   },
   {
      icon: 'user-group',
      title: t('web.tabs.users'),
      to: localePath(`/dashboard/${organizationId.value}/users`),
      visible: isOffice.value,
      group: 'people',
   },
   {
      icon: 'shield-check',
      title: t('web.tabs.inspectors'),
      to: localePath(`/dashboard/${organizationId.value}/inspectors`),
      visible: true,
      group: 'people',
   },
   {
      icon: 'arrows-pointing-in',
      title: t('web.tabs.groups'),
      to: localePath(`/dashboard/${organizationId.value}/groups`),
      visible: isOffice.value,
      group: 'people',
   },
   {
      icon: 'filter',
      title: t('web.tabs.specie'),
      to: localePath(`/dashboard/${organizationId.value}/specie`),
      visible: isOffice.value,
      group: 'rules',
   },
   {
      icon: 'document-text',
      title: t('web.tabs.abilitation'),
      to: localePath(`/dashboard/${organizationId.value}/abilitation`),
      visible: isOffice.value,
      group: 'rules',
   },
   {
      icon: 'document-text',
      title: t('web.tabs.ordinance'),
      to: localePath(`/dashboard/${organizationId.value}/ordinance`),
      visible: isOffice.value,
      group: 'rules',
   },
   {
      icon: 'cube',
      title: t('web.tabs.role'),
      to: localePath(`/dashboard/${organizationId.value}/role`),
      visible: isOffice.value && organizationId.value === 'org_PwttcUFpJhbsvX',
      group: 'admin',
   },
   {
      icon: 'language',
      title: t('web.tabs.localization'),
      to: localePath(`/dashboard/${organizationId.value}/localization`),
      visible: isOffice.value && organizationId.value === 'org_PwttcUFpJhbsvX',
      group: 'admin',
   },
])

const menuGroups = [
   { key: 'waters', icon: 'map', titleKey: 'web.tabs-groups.waters' },
   { key: 'cultivation', icon: 'document-check', titleKey: 'web.tabs-groups.cultivation' },
   { key: 'people', icon: 'users', titleKey: 'web.tabs-groups.people' },
   { key: 'rules', icon: 'document-text', titleKey: 'web.tabs-groups.rules' },
   { key: 'admin', icon: 'cube', titleKey: 'web.tabs-groups.admin' },
] as const

const ungroupedMenu = computed(() => menu.filter((item) => !item.group))

const menuSections = computed(() =>
   menuGroups
      .map((group) => ({
         key: group.key,
         icon: group.icon,
         title: t(group.titleKey),
         items: menu.filter((item) => item.group === group.key && item.visible),
      }))
      .filter((section) => section.items.length > 0)
)

const activeGroupKey = computed(() => menu.find((item) => item.to && selected(item.to))?.group)

watch(
   activeGroupKey,
   (key) => {
      if (key) sidebarStore.openGroup(key)
   },
   { immediate: true }
)

const dropdown = useMenuItemData(() => [
   {
      icon: 'identification',
      title: t('web.tabs.organization'),
      action: () => {
         organizationModalOpen.value = true
      },
   },
   {
      icon: 'lifebuoy',
      title: t('web.tabs.support'),
      to: 'https://chat.whatsapp.com/I30ufoctF0h7GWTZO1vl9l',
      blank: true,
   },
])

watch(
   () => route.fullPath,
   () => {
      sidebarStore.$state.open = false
      impersonateModalOpen.value = false
      organizationModalOpen.value = false
      notificationsOpen.value = false
   },
   { immediate: true }
)
</script>

<style lang="postcss" scoped>
.menu-tree {
   padding-left: 1.75rem;
}

.menu-tree__item {
   position: relative;
}

.menu-tree__item::before {
   content: '';
   position: absolute;
   top: 0;
   left: -0.625rem;
   height: 100%;
   border-left: 1px solid var(--color-grey);
   opacity: 0.3;
}

.menu-tree__item:last-child::before {
   height: 50%;
}

.menu-tree__item::after {
   content: '';
   position: absolute;
   top: 50%;
   left: -0.625rem;
   width: 0.625rem;
   border-top: 1px solid var(--color-grey);
   opacity: 0.3;
}
</style>
